/**
 * A speaker's lines (epic PLAT-11994 phase 3, DE12–DE13).
 *
 * A speaker holds facts — First Name, Last Name, Position (`role`), Company
 * (`organization`) — and two lines a nametag shows: Name and Title (the one
 * line under the name). A line someone typed is shown as typed; an empty line
 * is derived from the facts in the workspace's Speaker Format
 * (`branding.speaker_format`), whose default is exactly what every nametag
 * showed before: "First Last" and "Company | Position".
 *
 * Phase 2's per-template composer below (`RoleLineSettings`,
 * `composeRoleLine`) is kept only as the legacy reference the audit and
 * restore scripts compare against, and for the deprecated `roleLine` field
 * some `dev` templates still carry; no speaker line is built with it.
 */
import { isRoleDescription } from '@src/utility/speakerIdentification';

export type RoleLineOrder = 'company-role' | 'role-company';
export type RoleLineSeparator = ',' | '·' | '|';
export interface RoleLineSettings {
  order: RoleLineOrder;
  separator: RoleLineSeparator;
}

const ROLE_LINE_ORDERS: ReadonlyArray<{
  value: RoleLineOrder;
  label: string;
}> = [
  { value: 'company-role', label: 'Company, Role' },
  { value: 'role-company', label: 'Role, Company' },
];

const ROLE_LINE_SEPARATORS: ReadonlyArray<RoleLineSeparator> = [',', '·', '|'];

export const DEFAULT_ROLE_LINE: RoleLineSettings = {
  order: 'company-role',
  separator: '|',
};

function isRoleLineOrder(value: unknown): value is RoleLineOrder {
  return ROLE_LINE_ORDERS.some(o => o.value === value);
}

function isRoleLineSeparator(value: unknown): value is RoleLineSeparator {
  return ROLE_LINE_SEPARATORS.some(separator => separator === value);
}

/** A stored settings object, which may be partial or malformed, made whole. */
export function normalizeRoleLineSettings(
  settings: Partial<RoleLineSettings> | null | undefined,
): RoleLineSettings {
  const order = settings?.order;
  const separator = settings?.separator;
  return {
    order: isRoleLineOrder(order) ? order : DEFAULT_ROLE_LINE.order,
    separator: isRoleLineSeparator(separator)
      ? separator
      : DEFAULT_ROLE_LINE.separator,
  };
}

/**
 * The composed role line a two-line nametag shows. Empty parts drop out, so a
 * speaker with only a role or only a company reads as that alone, and a
 * stored combined title ("Acme Corp | CEO", no company) passes
 * through verbatim. A comma binds tight ("Role, Company"); the dot and the
 * bar get a space each side.
 */
export function composeRoleLine(
  speaker: { title?: string | null; organization?: string | null },
  settings: Partial<RoleLineSettings> | null | undefined = DEFAULT_ROLE_LINE,
): string {
  const { order, separator } = normalizeRoleLineSettings(settings);
  const role = speaker.title?.trim() ?? '';
  const company = speaker.organization?.trim() ?? '';
  const parts = order === 'company-role' ? [company, role] : [role, company];
  const joiner = separator === ',' ? ', ' : ` ${separator} `;
  return parts.filter(Boolean).join(joiner);
}

// ── The workspace's Speaker Format (DE13) ────────────────────────────────

export type SpeakerNameOrder = 'first-last' | 'last-first';
export type SpeakerTitleOrder = 'company-position' | 'position-company';
export type SpeakerSeparator = ',' | '·' | '|';
export interface SpeakerFormat {
  name: SpeakerNameOrder;
  titleOrder: SpeakerTitleOrder;
  titleSeparator: SpeakerSeparator;
}

/** Today's output: "First Last" and "Company | Position". */
export const DEFAULT_SPEAKER_FORMAT: SpeakerFormat = {
  name: 'first-last',
  titleOrder: 'company-position',
  titleSeparator: '|',
};

const SPEAKER_NAME_ORDERS: ReadonlyArray<SpeakerNameOrder> = [
  'first-last',
  'last-first',
];
const SPEAKER_TITLE_ORDERS: ReadonlyArray<SpeakerTitleOrder> = [
  'company-position',
  'position-company',
];
const SPEAKER_SEPARATORS: ReadonlyArray<SpeakerSeparator> = [',', '·', '|'];

const oneOf = <T extends string>(
  options: ReadonlyArray<T>,
  value: unknown,
  fallback: T,
): T => options.find(o => o === value) ?? fallback;

/**
 * A stored Speaker Format, which may be absent, a JSON string, partial or
 * malformed, made whole. Anything unreadable falls back to the default, so a
 * bad value can never blank a line.
 */
export function normalizeSpeakerFormat(raw: unknown): SpeakerFormat {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      value = null;
    }
  }
  const v = (value && typeof value === 'object' ? value : {}) as Record<
    string,
    unknown
  >;
  return {
    name: oneOf(SPEAKER_NAME_ORDERS, v.name, DEFAULT_SPEAKER_FORMAT.name),
    titleOrder: oneOf(
      SPEAKER_TITLE_ORDERS,
      v.titleOrder,
      DEFAULT_SPEAKER_FORMAT.titleOrder,
    ),
    titleSeparator: oneOf(
      SPEAKER_SEPARATORS,
      v.titleSeparator,
      DEFAULT_SPEAKER_FORMAT.titleSeparator,
    ),
  };
}

const text = (v: string | null | undefined) => v?.trim() ?? '';

// ── A speaker's lines (DE12) ─────────────────────────────────────────────

/**
 * The name derived from a speaker's name parts: "First Last", or "Last,
 * First" in that format, or whichever part is set. '' when neither is.
 */
export function deriveSpeakerName(
  parts: { firstName?: string | null; lastName?: string | null },
  format: unknown = DEFAULT_SPEAKER_FORMAT,
): string {
  const first = text(parts.firstName);
  const last = text(parts.lastName);
  if (first && last && normalizeSpeakerFormat(format).name === 'last-first')
    return `${last}, ${first}`;
  return [first, last].filter(Boolean).join(' ');
}

// Honorifics and suffixes: a word like these is not a first or last name.
const NAME_AFFIXES = new Set([
  'mr',
  'mrs',
  'ms',
  'mx',
  'dr',
  'prof',
  'sir',
  'mme',
  'rev',
  'fr',
  'hon',
  'sen',
  'rep',
  'gov',
  'capt',
  'col',
  'gen',
  'phd',
  'md',
  'esq',
  'jr',
  'sr',
  'ii',
  'iii',
  'iv',
]);

/**
 * [PLAT-12214] A stored full name split into First and Last Name, or null
 * when it can't be split safely. "Word Word" splits into both; a single word
 * is a First Name alone. Only when the parts join back to the (trimmed)
 * stored name, so once a speaker has them the modal reads its Name as
 * derived and a save keeps the same text. Anything else (three or more
 * words, a comma, a "|", a digit, an honorific or suffix, a double space, a
 * role label like "Narrator" or "Acme spokesperson") is left to a person.
 */
export function splitSpeakerName(
  name: string | null | undefined,
): { firstName: string; lastName: string } | null {
  const stored = text(name);
  const words = stored.split(' ');
  if (words.length > 2) return null;
  const [firstName, lastName = ''] = words;
  const clean = (word: string) =>
    word !== '' &&
    !/[\s,|\d()&/@]/.test(word) &&
    !NAME_AFFIXES.has(word.replace(/\.$/, '').toLowerCase());
  if (!clean(firstName) || (words.length === 2 && !clean(lastName)))
    return null;
  if (isRoleDescription({ name: stored })) return null;
  if (deriveSpeakerName({ firstName, lastName }) !== stored) return null;
  return { firstName, lastName };
}

/** The Title derived from Position and Company in the format; '' if neither. */
export function deriveSpeakerTitle(
  facts: { role?: string | null; organization?: string | null },
  format: unknown = DEFAULT_SPEAKER_FORMAT,
): string {
  const { titleOrder, titleSeparator } = normalizeSpeakerFormat(format);
  const position = text(facts.role);
  const company = text(facts.organization);
  const parts =
    titleOrder === 'company-position'
      ? [company, position]
      : [position, company];
  const joiner = titleSeparator === ',' ? ', ' : ` ${titleSeparator} `;
  return parts.filter(Boolean).join(joiner);
}

/** The Name line: the stored name if any, else derived from the parts. */
export function speakerNameLine(
  speaker: {
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  },
  format: unknown = DEFAULT_SPEAKER_FORMAT,
): string {
  return text(speaker.name) || deriveSpeakerName(speaker, format);
}

/**
 * The Title line, the one line under the name: the stored Title as typed if
 * any — every speaker that predates phase 3 has one, so their nametags don't
 * change — else Position and Company in the format.
 */
export function speakerTitleLine(
  speaker: {
    title?: string | null;
    role?: string | null;
    organization?: string | null;
  },
  format: unknown = DEFAULT_SPEAKER_FORMAT,
): string {
  return text(speaker.title) || deriveSpeakerTitle(speaker, format);
}

/**
 * A speaker's Title line in the default format, for the readers that don't
 * know the workspace: ID Speaker, fuzzy matching and the prompt lines. Pull
 * quotes and quote cards pass the workspace's format to `speakerTitleLine`;
 * Generate Bio / Generate Voice pass it through `formatSpeakerRole`
 * (PLAT-12261).
 */
export function speakerIdentificationRoleLine(speaker: {
  title?: string | null;
  role?: string | null;
  organization?: string | null;
}): string {
  return speakerTitleLine(speaker);
}

/**
 * A speaker's Title as lists show it: the stored Title, else Position and
 * Company in the workspace's Speaker Format, exactly as a nametag shows it
 * (PLAT-12261; it was a fixed "Position · Company" since PLAT-11997). One
 * formatter so the Speaker Manager, the Speakers panel, Assign Speaker and
 * the speaker selector cannot drift apart.
 */
export function formatSpeakerRole(
  speaker: {
    title?: string | null;
    role?: string | null;
    organization?: string | null;
  },
  format: unknown = DEFAULT_SPEAKER_FORMAT,
): string {
  return speakerTitleLine(speaker, format);
}
